import { createAsyncThunk } from '@reduxjs/toolkit'
import { searchDoctors, getDoctorsPage, getDoctorAvailability, bookAppointment } from '../api/searchApi'
import {
  performSearchStart,
  performSearchSuccess,
  performSearchFailure,
  changePageStart,
  changePageSuccess,
  changePageFailure,
  addToSearchHistory,
  setSearchFilters
} from './searchReducer'
import {
  fetchAvailabilityStart,
  fetchAvailabilitySuccess,
  fetchAvailabilityFailure,
  confirmBookingStart,
  confirmBookingSuccess,
  confirmBookingFailure
} from './bookingReducer'

// Search thunks
export const performSearch = createAsyncThunk(
  'search/performSearch',
  async (filters, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(performSearchStart())

      console.log('PerformSearch - Using filters:', filters)

      // Save all filters to Redux state to ensure pagination works correctly
      dispatch(setSearchFilters(filters))

      const data = await searchDoctors(filters)

      let processedData
      // Handle different response formats
      if (Array.isArray(data)) {
        processedData = {
          results: data,
          pagination: {
            currentPage: 1,
            totalResults: data.length,
            totalPages: Math.ceil(data.length / 20),
            resultsPerPage: 20
          }
        }
      } else if (data.results) {
        // Use doctorCount from API response for pagination calculations
        const totalResults = data.doctorCount || data.totalCount || data.results.length
        const resultsPerPage = data.pagination?.resultsPerPage || 20
        console.log('totalResults', totalResults)
        console.log('resultsPerPage', resultsPerPage)
        processedData = {
          results: data.results,
          pagination: {
            currentPage: data.pagination?.currentPage || 1,
            totalPages: data.pagination?.totalPages || Math.ceil(totalResults / resultsPerPage),
            totalResults: totalResults,
            resultsPerPage: resultsPerPage
          }
        }
      } else {
        processedData = {
          results: data,
          pagination: {
            currentPage: 1,
            totalResults: Array.isArray(data) ? data.length : 0,
            totalPages: 1,
            resultsPerPage: 20
          }
        }
      }

      dispatch(performSearchSuccess(processedData))

      // Add to search history
      dispatch(
        addToSearchHistory({
          filters,
          resultsCount: processedData.results.length
        })
      )

      return processedData
    } catch (error) {
      console.error('Search failed:', error)
      const errorMessage = error.message || 'Search failed'
      dispatch(performSearchFailure(errorMessage))
      return rejectWithValue(errorMessage)
    }
  }
)

export const changePage = createAsyncThunk(
  'search/changePage',
  async (page, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(changePageStart())

      const { searchEngine } = getState()
      const filters = searchEngine.searchFilters

      console.log('ChangePage - Using filters:', filters)
      console.log('ChangePage - Going to page:', page)

      const data = await getDoctorsPage(page, filters)

      let processedData
      // Handle different response formats
      if (Array.isArray(data)) {
        processedData = {
          results: data,
          page
        }
      } else if (data.results) {
        // Use doctorCount from API response for pagination calculations
        const totalResults = data.doctorCount || data.pagination?.totalResults || data.results.length
        const resultsPerPage = data.pagination?.resultsPerPage || 20

        processedData = {
          results: data.results,
          pagination: {
            currentPage: data.pagination?.currentPage || page,
            totalPages: data.pagination?.totalPages || Math.ceil(totalResults / resultsPerPage),
            totalResults: totalResults,
            resultsPerPage: resultsPerPage
          }
        }
      } else {
        processedData = {
          results: data,
          page
        }
      }

      dispatch(changePageSuccess(processedData))

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })

      return processedData
    } catch (error) {
      console.error('Page change failed:', error)
      const errorMessage = error.message || 'Page change failed'
      dispatch(changePageFailure(errorMessage))
      return rejectWithValue(errorMessage)
    }
  }
)

// Booking thunks
export const fetchAvailability = createAsyncThunk(
  'booking/fetchAvailability',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { searchEngineBooking } = getState()
      const { selectedDoctor, currentWeekStart, hasInitializedWeek } = searchEngineBooking

      if (!selectedDoctor) {
        return rejectWithValue('No doctor selected')
      }

      dispatch(fetchAvailabilityStart())

      const filter = {
        medicalProfessional: selectedDoctor._id,
        clinicRef: selectedDoctor.clinicRecord?._id
      }

      const availability = await getDoctorAvailability(filter)

      const result = { availability }
      dispatch(fetchAvailabilitySuccess(result))

      return result
    } catch (error) {
      console.error('Error fetching availability:', error)
      const errorMessage = error.message || 'Failed to fetch availability'
      dispatch(fetchAvailabilityFailure(errorMessage))
      return rejectWithValue(errorMessage)
    }
  }
)

export const confirmBooking = createAsyncThunk(
  'booking/confirmBooking',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { searchEngineBooking } = getState()
      const { selectedDoctor, selectedSlot, selectedReason, agreedToTerms } = searchEngineBooking

      if (!agreedToTerms || !selectedSlot || !selectedReason || !selectedDoctor) {
        return rejectWithValue('Missing required booking information')
      }

      dispatch(confirmBookingStart())

      // Calculate end time (15 minutes after start)
      const calculateEndTime = (startTime) => {
        const [hours, minutes] = startTime.split(':').map(Number)
        const startDate = new Date()
        startDate.setHours(hours, minutes, 0, 0)

        const endDate = new Date(startDate.getTime() + 15 * 60000) // Add 15 minutes
        return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`
      }

      const bookingData = {
        clinicRecord: selectedDoctor.clinicRecord,
        medicalProfRef: selectedDoctor._id,
        bookingDate: selectedSlot.date,
        reason: selectedReason,
        startTime: selectedSlot.time,
        endTime: calculateEndTime(selectedSlot.time)
      }

      const newBooking = await bookAppointment(bookingData)

      dispatch(confirmBookingSuccess(newBooking))

      return newBooking
    } catch (error) {
      console.error('Error confirming booking:', error)
      const errorMessage = error.message || 'Failed to confirm booking'
      dispatch(confirmBookingFailure(errorMessage))
      return rejectWithValue(errorMessage)
    }
  }
)

// Combined thunk for opening availability view and fetching data
export const openAvailabilityViewAndFetch = createAsyncThunk(
  'booking/openAvailabilityViewAndFetch',
  async (doctor, { dispatch }) => {
    // First open the availability view
    dispatch({ type: 'booking/openAvailabilityView', payload: doctor })

    // Then fetch availability data
    return dispatch(fetchAvailability())
  }
)

// Utility thunk for search with filter update
export const searchWithFilters = createAsyncThunk(
  'search/searchWithFilters',
  async (newFilters, { dispatch, getState }) => {
    const { searchEngine } = getState()
    const updatedFilters = { ...searchEngine.searchFilters, ...newFilters }

    // Update filters first
    dispatch({ type: 'search/setSearchFilters', payload: updatedFilters })

    // Then perform search
    return dispatch(performSearch(updatedFilters))
  }
)
